import React from 'react';
import classNames from 'classnames';
import styles from './Loader.module.scss';
import { Phrases } from '../../utilities/constants/phrases';
import Icon, { IconGlyph } from '../Icon';
import DynamicTexts from '../DynamicTexts/DynamicTexts';

export enum LoaderType {
  FULL_PAGE = 'FULL_PAGE',
  EDITOR = 'EDITOR',
}

export const LoaderSize = {
  LOADER_SIZE_S: {
    width: 24,
    height: 24,
  },
  LOADER_SIZE_M: {
    width: 36,
    height: 36,
  },
};

const icons: {
  [key in IconGlyph]?: React.ReactNode;
} = {
  [IconGlyph.EDITOR_LOADER]: <Icon glyph={IconGlyph.EDITOR_LOADER} />,
  [IconGlyph.LOADER]: <Icon glyph={IconGlyph.LOADER} />,
};

const loaderEditorTitles = [
  Phrases.LOADER_EDITOR_TITLE_1,
  Phrases.LOADER_EDITOR_TITLE_2,
  Phrases.LOADER_EDITOR_TITLE_3,
  Phrases.LOADER_EDITOR_TITLE_4,
  Phrases.LOADER_EDITOR_TITLE_5,
  Phrases.LOADER_EDITOR_TITLE_6,
];

interface ILoader {
  width: number;
  height: number;
  iconColor: string;
  iconName?: IconGlyph;
  modifier?: string;
  markup?: JSX.Element;
}

export const getLoader = (loaderType: LoaderType): ILoader => {
  switch (loaderType) {
    case LoaderType.FULL_PAGE: {
      const { width, height } = LoaderSize.LOADER_SIZE_M;
      return {
        width,
        height,
        modifier: 'full-page',
        iconColor: 'primary-accent',
      };
    }
    case LoaderType.EDITOR: {
      const { width, height } = LoaderSize.LOADER_SIZE_S;
      return {
        width,
        height,
        modifier: 'editor',
        iconColor: 'primary-accent',
        iconName: IconGlyph.EDITOR_LOADER,
        markup: (
          <>
            <DynamicTexts
              texts={loaderEditorTitles}
              className='text-base fw-bold mt-xxxs c-main ff-secondary ta-center'
            />
            <p className='text-sm mt-xxxxxs c-main-lighter'>
              {Phrases.LOADER_EDITOR_LABEL}
            </p>
          </>
        ),
      };
    }
    default: {
      const { width, height } = LoaderSize.LOADER_SIZE_M;
      return {
        width,
        height,
        iconColor: 'white',
      };
    }
  }
};

interface Props {
  type: LoaderType;
  className?: string;
}

const Loader: React.FC<Props> = ({ type, className }) => {
  const {
    width: iconWidth,
    height: iconHeight,
    modifier,
    iconName = IconGlyph.LOADER,
    iconColor,
    markup = null,
  } = getLoader(type);

  return (
    <div className={classNames(className, modifier && styles[modifier])}>
      <div
        className={classNames(styles.inner, `c-${iconColor}`)}
        style={{
          width: `${iconWidth}px`,
          height: `${iconHeight}px`,
        }}>
        {Phrases.LOADER_LABEL}
        {icons[iconName]}
      </div>
      {markup}
    </div>
  );
};

export default Loader;
